import type { i18n as I18nInstance } from "i18next";

/**
 * I18N-05 — RTL `<html dir>` driver.
 *
 * Listens to i18next's `languageChanged` event and synchronously toggles:
 *   1. `<html dir>`   — "rtl" for Arabic, "ltr" otherwise
 *   2. `<html lang>`  — the active BCP-47 locale
 *   3. `<body class>` — `font-arabic` / `font-latin` (coordinates with
 *      `packages/design-system/src/tokens/fonts.css` which ships both classes)
 *
 * We also apply once on init so the pre-mount locale (resolved by the
 * i18next-browser-languagedetector from cookie → localStorage → navigator)
 * is reflected in the DOM before React renders — otherwise the first paint
 * would show LTR and then snap into RTL.
 *
 * Works in SSR / node by guarding `typeof document`.
 */
export function attachDirectionDriver(i18n: I18nInstance): void {
  const apply = (lng: string): void => {
    if (typeof document === "undefined") return;
    const isArabic = lng.startsWith("ar");
    const dir: "rtl" | "ltr" = isArabic ? "rtl" : "ltr";
    document.documentElement.dir = dir;
    document.documentElement.lang = lng;
    document.body.classList.toggle("font-arabic", isArabic);
    document.body.classList.toggle("font-latin", !isArabic);
  };

  i18n.on("languageChanged", apply);
  // Apply once on init so first paint matches the detector-resolved locale.
  apply(i18n.language);
}

/** Pure helper: "rtl" iff the locale is Arabic. */
export function currentDirection(locale: string): "rtl" | "ltr" {
  return locale.startsWith("ar") ? "rtl" : "ltr";
}
